// Libs
import React from "react";
import clsx from "clsx";

// Types
import { Driver } from "@/types/supabase";

// Hooks
import { useAuth } from "@/hooks/useAuth";
import { useDriverForm } from "@/hooks/useDriverForm";
import { useDriverSync } from "@/hooks/useDriverSync";

// Components
import { Card, CardTitle, CardContent } from "@/components/atoms/card";
import { Button } from "@/components/atoms/button";
import { Form } from "@/components/atoms/form";
import { Loader2, Save, Pencil, User } from "lucide-react";
import { DriverAvatar } from "@/components/drivers/DriverAvatar";
import { PersonalInfoForm } from "@/components/drivers/PersonalInfoForm";
import { ResumeFormDialog } from "@/components/drivers/ResumeFormDialog";
import { DeviceInfoCard } from "@/components/drivers/DeviceInfoCard";
import { usePermissions } from "@/contexts/PermissionContext";

interface DriverInfoPersonalRefactoredProps {
  driver: Driver;
  setDriver: (driver: Driver) => void;
  realTimeData: any;
}

export function DriverInfoPersonalRefactored({
  driver,
  setDriver,
  realTimeData,
}: DriverInfoPersonalRefactoredProps) {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();

  // WebSocket sync for cross-browser notifications
  const { notifyDriverUpdate } = useDriverSync({
    driverId: String(driver.id),
    userNickname: String(user?.nickname || "anonymous"),
  });

  const {
    form,
    isEditing,
    isLoading,
    showResumeDialog,
    savedFormData,
    handleEdit,
    handleCancel,
    handleSave,
    handleResumeEditing,
    handleDiscardSavedData,
  } = useDriverForm({
    driver,
    setDriver,
    onUpdate: () => {
      // Notify other browsers/tabs when driver is updated
      const userName = user?.name || user?.email || "Usuario anónimo";
      notifyDriverUpdate(userName);
    },
  });

  // Image upload state and methods from DriverAvatar
  const [imageUploadState, setImageUploadState] = React.useState({
    isProcessing: false,
  });
  const imageMethodsRef = React.useRef<{
    resetImageSelection: () => void;
    hasSelectedImage: () => boolean;
    uploadImage?: () => Promise<void>;
  } | null>(null);

  const handleImageUploadStateChange = React.useCallback(
    (isProcessing: boolean) => {
      setImageUploadState((prev) =>
        prev.isProcessing === isProcessing ? prev : { isProcessing },
      );
    },
    [],
  );

  const handleImageMethodsReady = React.useCallback(
    (methods: {
      resetImageSelection: () => void;
      hasSelectedImage: () => boolean;
      uploadImage?: () => Promise<void>;
    }) => {
      imageMethodsRef.current = methods;
    },
    [],
  );

  // Enhanced save handler that also handles image upload
  const handleSaveWithImage = async (data: any) => {
    const imageMethods = imageMethodsRef.current;
    const hasImageSelected = imageMethods?.hasSelectedImage() || false;

    try {
      // Check if we have any changes (form fields or image)
      await handleSave(data);

      if (hasImageSelected) {
        // Upload image if one is selected
        if (imageMethods && imageMethods.uploadImage) {
          setImageUploadState({ isProcessing: true });
          await imageMethods.uploadImage();
        }
        imageMethods?.resetImageSelection();
      }
    } catch (error) {
      // Form save error is already handled in handleSave
      console.error("Error in save process:", error);
    } finally {
      if (hasImageSelected) {
        setImageUploadState({ isProcessing: false });
      }
    }
  };

  // Enhanced cancel handler that also resets image selection
  const handleCancelWithImage = () => {
    handleCancel();
    imageMethodsRef.current?.resetImageSelection();
  };

  const isLoadingAny = isLoading || imageUploadState.isProcessing;

  return (
    <div className="space-y-4 mt-4 pb-6">
      {/* Resume Form Dialog */}
      <ResumeFormDialog
        showResumeDialog={showResumeDialog}
        setShowResumeDialog={() => {}}
        savedFormData={savedFormData}
        onResumeEditing={handleResumeEditing}
        onDiscardSavedData={handleDiscardSavedData}
      />

      {/* Loading Overlay */}
      {isLoadingAny && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-hidden">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4 shadow-xl max-w-sm mx-4">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            <div className="text-center">
              <p className="text-gray-900 font-medium">
                {imageUploadState.isProcessing
                  ? "Procesando imagen..."
                  : "Guardando cambios..."}
              </p>
              <p className="text-gray-600 text-sm mt-1">
                {imageUploadState.isProcessing
                  ? "Optimizando y validando la imagen"
                  : "Por favor espera un momento"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header Card */}
      <Card className="bg-white shadow-sm border border-gray-200">
        <div className="p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-gray-600" />
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="text-base font-semibold text-gray-900 mb-0">
                Información personal
              </CardTitle>
              <div className="text-sm text-gray-600 font-normal mt-1">
                Información y datos sobre el conductor
              </div>
            </div>
          </div>

          {hasPermission("driver:update") && driver.id !== 19 && (
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button
                size="sm"
                variant="ghost"
                className={clsx(
                  "w-full sm:w-auto text-gray-600 border border-gray-300 hover:bg-gray-50",
                  !isEditing && "hidden",
                )}
                onClick={handleCancelWithImage}
                disabled={isLoadingAny}
              >
                Cancelar
              </Button>

              <Button
                size="sm"
                className="w-full sm:w-auto bg-indigo-600 text-white hover:bg-indigo-700"
                onClick={() =>
                  isEditing
                    ? form.handleSubmit(handleSaveWithImage)()
                    : handleEdit()
                }
                disabled={isLoadingAny}
              >
                {isEditing ? (
                  isLoadingAny ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Guardar
                    </>
                  )
                ) : (
                  <>
                    <Pencil className="w-4 h-4 mr-2" />
                    Editar
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Form Card */}
      <Card className="bg-white">
        <CardContent className="!py-8">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSaveWithImage)}
              className="flex flex-col lg:flex-row gap-6 lg:gap-8"
            >
              {/* Avatar Component */}
              <DriverAvatar
                driver={driver}
                setDriver={setDriver}
                isEditing={isEditing}
                onSaveFormData={() => {
                  // Trigger form watch effect
                  form.getValues();
                }}
                onImageUploadStateChange={handleImageUploadStateChange}
                onImageMethodsReady={handleImageMethodsReady}
              />

              {/* Form Fields Component */}
              <PersonalInfoForm form={form} isEditing={isEditing} />
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Device Info Component */}
      <DeviceInfoCard driver={driver} realTimeData={realTimeData} />
    </div>
  );
}
